import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Switch,
    FormControlLabel,
    CircularProgress,
} from '@mui/material';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    getEditorialIntelligenceById,
    getAdminArticleById,
    deleteAdminArticle,
    getRelatedEditorialIntelligences,
    getNeoFeedContextSuggestions,
    writeAdminArticleStream,
} from '../../../utils/api';
import { createNotification } from '../../../utils/alerts';
import { getErrorMessage } from '../../../utils/errorUtils';
import ArticleWriter from '../../../components/admin/ArticleWriter';
import NeoFeedSuggestionsPanel from '../../../components/admin/ArticleWriter/NeoFeedSuggestionsPanel';
import RelatedNewsPanel from '../../../components/admin/ArticleWriter/RelatedNewsPanel';
import useNeoFeedContextSelection from '../../../components/admin/ArticleWriter/useNeoFeedContextSelection';
import { buildNeoFeedContextSourcesPayload } from '../../../components/admin/ArticleWriter/neoFeedContextUtils';

/* ─── Paleta ──────────────────────────────────────────────────── */
const PRIMARY      = '#f25d27';
const PRIMARY_DARK = '#d44e1f';

/* ─── Notícias relacionadas ───────────────────────────────────── */
const RELATED_PAGE_SIZE = 5;

/* ─── Página ─────────────────────────────────────────────────── */
export default function AdminWriteArticlePage() {
    const navigate  = useNavigate();
    const location  = useLocation();
    const { editorial_intelligence_id } = useParams();
    const isMobile  = useMediaQuery('(max-width:900px)');

    const listState = location.state || null;

    /* ── Dados base ── */
    const [item, setItem]               = useState(null);
    const [pageLoading, setPageLoading] = useState(true);
    const [articlesHistory, setArticlesHistory] = useState([]);

    // Id da matéria (admin/articles) vinculada a esta inteligência editorial,
    // vem do próprio retorno de `getEditorialIntelligenceById` (campo
    // `article_id`). Enquanto for nulo, a geração cria a primeira matéria
    // vinculada via `editorial_intelligence_id`.
    const [articleId, setArticleId] = useState(null);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    /* ── Notícias relacionadas (contexto opcional, anexável via modal de
       contexto do composer) ── */
    const [relatedNews, setRelatedNews] = useState([]);
    const [relatedNewsTotal, setRelatedNewsTotal] = useState(0);
    const [relatedNewsLoading, setRelatedNewsLoading] = useState(false);
    const [relatedNewsPage, setRelatedNewsPage] = useState(0);
    // Ids selecionados — mantidos em memória entre trocas de página
    const [selectedRelatedIds, setSelectedRelatedIds] = useState([]);
    // Cache de título/fonte dos itens selecionados — necessário porque a
    // lista paginada (`relatedNews`) só contém a página atual; sem isso, o
    // resumo do chip de contexto perderia o título de itens selecionados
    // em páginas anteriores.
    const [selectedRelatedItemsMap, setSelectedRelatedItemsMap] = useState({});

    /* ── Sugestões NeoFeed (contexto opcional, alternativo às notícias
       relacionadas) — buscadas automaticamente a cada versão gerada
       enquanto o toggle estiver ativo. Ambas as fontes ficam anexáveis via
       o modal "Adicionar contexto" do composer (abas). ── */
    const [neoFeedEnabled, setNeoFeedEnabled] = useState(true);
    const [neoFeedSuggestions, setNeoFeedSuggestions] = useState([]);
    const [neoFeedLoading, setNeoFeedLoading] = useState(false);
    const [neoFeedError, setNeoFeedError] = useState(null);
    const {
        selectedSources: selectedNeoFeedSources,
        toggleSuggestion: handleToggleNeoFeedSuggestion,
        removeSource: handleRemoveNeoFeedSource,
        toggleChunk: handleToggleNeoFeedChunk,
        changeUsageMode: handleChangeNeoFeedUsageMode,
        reset: resetNeoFeedSelection,
    } = useNeoFeedContextSelection({ forceUsageMode: 'factual' });

    /* ── Carregar dados iniciais ── */
    useEffect(() => {
        const loadAll = async () => {
            setPageLoading(true);
            try {
                const editorialData = await getEditorialIntelligenceById(editorial_intelligence_id);
                setItem(editorialData);

                const linkedArticleId = editorialData?.article_id ?? null;
                setArticleId(linkedArticleId);

                if (!linkedArticleId) {
                    setArticlesHistory([]);
                    return;
                }

                try {
                    const articleData = await getAdminArticleById(linkedArticleId);
                    const versions = Array.isArray(articleData?.versions) ? [...articleData.versions] : [];
                    versions.sort((a, b) => new Date(b.written_at) - new Date(a.written_at));
                    setArticlesHistory(versions);
                } catch (err) {
                    if (err.response?.status === 404) {
                        setArticleId(null);
                        setArticlesHistory([]);
                    } else if (err.response?.status !== 401) {
                        createNotification('error', 'Erro ao carregar histórico de matérias.', isMobile);
                    }
                }
            } catch (err) {
                if (err.response?.status === 404) {
                    createNotification('error', 'Inteligência editorial não encontrada.', isMobile);
                    navigate('/admin/editorial-intelligence');
                    return;
                } else if (err.response?.status !== 401) {
                    createNotification('error', 'Erro ao carregar dados.', isMobile);
                }
            } finally {
                setPageLoading(false);
            }
        };
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorial_intelligence_id]);

    /* ── Resetar página e seleção ao trocar de artigo (o toggle NeoFeed
       fica ligado por padrão) ── */
    useEffect(() => {
        setRelatedNewsPage(0);
        setSelectedRelatedIds([]);
        setSelectedRelatedItemsMap({});
        setNeoFeedEnabled(true);
        setNeoFeedSuggestions([]);
        setNeoFeedError(null);
        resetNeoFeedSelection();
    }, [editorial_intelligence_id, resetNeoFeedSelection]);

    /* ── Carregar notícias relacionadas (paginado no servidor, 5 por página) ── */
    useEffect(() => {
        const loadRelated = async () => {
            setRelatedNewsLoading(true);
            try {
                const data = await getRelatedEditorialIntelligences(editorial_intelligence_id, {
                    page: relatedNewsPage,
                    page_size: RELATED_PAGE_SIZE,
                });
                setRelatedNews(data.editorial_intelligences ?? []);
                setRelatedNewsTotal(data.total ?? 0);
            } catch (err) {
                if (err.response?.status !== 401) {
                    createNotification('error', 'Erro ao carregar notícias relacionadas.', isMobile);
                }
            } finally {
                setRelatedNewsLoading(false);
            }
        };
        loadRelated();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorial_intelligence_id, relatedNewsPage]);

    /* ── Selecionar/desselecionar notícia relacionada ── */
    const toggleRelatedNews = useCallback((id) => {
        setSelectedRelatedIds((prev) => (
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        ));
        setSelectedRelatedItemsMap((prev) => {
            if (prev[id]) {
                // já em cache — mantém (permite desselecionar e reselecionar sem perder o título)
                return prev;
            }
            const item = relatedNews.find((news) => news.id === id);
            if (!item) return prev;
            return { ...prev, [id]: { title: item.title, source: item.source } };
        });
    }, [relatedNews]);

    const handleClearRelatedSelection = useCallback(() => {
        setSelectedRelatedIds([]);
        setSelectedRelatedItemsMap({});
    }, []);

    /* ── Sugestões NeoFeed — busca pelo id da inteligência editorial e, uma
       vez conhecido, também pelo id da matéria vinculada ── */
    const fetchNeoFeedSuggestions = useCallback(async (articleIdOverride) => {
        setNeoFeedLoading(true);
        setNeoFeedError(null);
        try {
            const body = { editorial_intelligence_id };
            const effectiveArticleId = articleIdOverride ?? articleId;
            if (effectiveArticleId) body.article_id = effectiveArticleId;

            const data = await getNeoFeedContextSuggestions(body);
            setNeoFeedSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
        } catch (err) {
            const message = getErrorMessage(err, 'Erro ao buscar sugestões no acervo NeoFeed.');
            setNeoFeedError(message);
            if (err.response?.status !== 401) {
                createNotification('error', message, isMobile);
            }
        } finally {
            setNeoFeedLoading(false);
        }
    }, [editorial_intelligence_id, articleId, isMobile]);

    /* ── Como o toggle vem ligado por padrão, busca automaticamente quando
       os dados da página terminam de carregar (ou quando o toggle é
       reativado depois de desligado) ── */
    useEffect(() => {
        if (!pageLoading && neoFeedEnabled) {
            fetchNeoFeedSuggestions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageLoading, neoFeedEnabled, editorial_intelligence_id]);

    const handleToggleNeoFeedEnabled = useCallback((event) => {
        const checked = event.target.checked;
        setNeoFeedEnabled(checked);
        if (checked) {
            // A busca é disparada pelo efeito acima, reagindo à mudança de `neoFeedEnabled`.
        } else {
            setNeoFeedSuggestions([]);
            setNeoFeedError(null);
            resetNeoFeedSelection();
        }
    }, [resetNeoFeedSelection]);

    const handleBack = () => navigate('/admin/articles');

    const handleGoToAnalysis = () => navigate(
        `/admin/editorial-intelligence/${editorial_intelligence_id}`,
        { state: listState }
    );

    // O endpoint dedicado de escrita por inteligência editorial não existe
    // mais — tudo passa por `/admin/articles/write`. Informar sempre o
    // `editorial_intelligence_id` garante que o backend crie (na primeira
    // vez) ou continue (nas próximas) a matéria vinculada a esta
    // inteligência editorial; `article_id` também é enviado quando já
    // conhecido, para reforçar a referência.
    const streamFn = useCallback((payload, options) => (
        writeAdminArticleStream(
            { ...payload, editorial_intelligence_id, article_id: articleId },
            options
        )
    ), [editorial_intelligence_id, articleId]);

    const handleGenerated = useCallback((data) => {
        let effectiveArticleId = articleId;
        if (!articleId) {
            const newId = data?.article_id ?? data?.version_id ?? null;
            if (newId) {
                setArticleId(newId);
                effectiveArticleId = newId;
            }
        }
        if (neoFeedEnabled) {
            fetchNeoFeedSuggestions(effectiveArticleId);
        }
    }, [articleId, neoFeedEnabled, fetchNeoFeedSuggestions]);

    const handleDelete = async () => {
        if (!articleId) return;
        setDeleteLoading(true);
        try {
            await deleteAdminArticle(articleId);
            createNotification('success', 'Matéria excluída com sucesso!', isMobile);
            navigate('/admin/articles');
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', getErrorMessage(err, 'Erro ao excluir matéria.'), isMobile);
            }
        } finally {
            setDeleteLoading(false);
            setConfirmDeleteOpen(false);
        }
    };

    const latestVersionTitle = articlesHistory.length > 0 ? articlesHistory[0].title : null;

    const extraTopActions = item ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
                variant="contained"
                startIcon={<AnalyticsIcon fontSize="small" />}
                onClick={handleGoToAnalysis}
                sx={{
                    backgroundColor: PRIMARY,
                    '&:hover': { backgroundColor: PRIMARY_DARK },
                    textTransform: 'none',
                    boxShadow: 'none',
                    fontWeight: 600,
                }}
            >
                Acessar análise editorial
            </Button>
            {articleId && (
                <Button
                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                    onClick={() => setConfirmDeleteOpen(true)}
                    sx={{
                        color: '#d32f2f', textTransform: 'none', fontWeight: 500,
                        '&:hover': { backgroundColor: 'rgba(211,47,47,0.06)' },
                    }}
                >
                    Excluir matéria
                </Button>
            )}
        </Box>
    ) : null;

    const neoFeedContextSourcesPayload = buildNeoFeedContextSourcesPayload(selectedNeoFeedSources);
    const extraPayload = {
        ...(selectedRelatedIds.length ? { related_editorial_intelligence_ids: selectedRelatedIds } : {}),
        ...(neoFeedContextSourcesPayload.length
            ? { neofeed_context_sources: JSON.stringify(neoFeedContextSourcesPayload) }
            : {}),
    };

    const latestArticle = articlesHistory.length > 0 ? articlesHistory[0] : null;

    const headerContent = item && (
        <Paper sx={{
            p: '14px 20px',
            borderRadius: '10px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            borderLeft: `4px solid ${PRIMARY}`,
        }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 0.5 }}>
                <Chip label={item.source} size="small"
                    sx={{ backgroundColor: PRIMARY, color: '#fff', fontWeight: 600 }} />
                {item.sector && (
                    <Chip label={item.sector} size="small" variant="outlined"
                        sx={{ borderColor: '#ddd', color: '#555' }} />
                )}
                {item.publication_recommendation?.primary_area && (
                    <Chip label={item.publication_recommendation.primary_area}
                        size="small" variant="outlined"
                        sx={{ borderColor: PRIMARY, color: PRIMARY, fontWeight: 600 }} />
                )}
            </Box>
            <Typography sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '0.95rem', lineHeight: 1.4 }}>
                {item.title}
            </Typography>
            {item.theme && (
                <Typography sx={{ color: '#777', fontSize: '0.82rem', mt: 0.3 }}>
                    {item.theme}
                </Typography>
            )}
        </Paper>
    );

    const neoFeedToggleControl = (
        <Paper sx={{ borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', p: '6px 14px', mb: 1 }}>
            <FormControlLabel
                control={
                    <Switch
                        size="small"
                        checked={neoFeedEnabled}
                        onChange={handleToggleNeoFeedEnabled}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY },
                        }}
                    />
                }
                label={
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#333' }}>
                        Usar base NeoFeed
                    </Typography>
                }
                sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
            />
        </Paper>
    );

    const selectedNeoFeedCount = Object.keys(selectedNeoFeedSources).length;
    const selectedRelatedSummaryItems = selectedRelatedIds.map((id) => (
        selectedRelatedItemsMap[id]?.title || `Notícia #${id}`
    ));

    const contextSources = [
        {
            key: 'neofeed',
            label: 'Sugestões NeoFeed',
            selectedCount: selectedNeoFeedCount,
            summaryItems: Object.values(selectedNeoFeedSources)
                .map((entry) => entry.suggestion?.title)
                .filter(Boolean),
            onClearAll: resetNeoFeedSelection,
            topControl: neoFeedToggleControl,
            panel: neoFeedEnabled ? (
                <NeoFeedSuggestionsPanel
                    embedded
                    expanded
                    onToggleExpanded={() => {}}
                    suggestions={neoFeedSuggestions}
                    loading={neoFeedLoading}
                    error={neoFeedError}
                    selectedSources={selectedNeoFeedSources}
                    onToggleSuggestion={handleToggleNeoFeedSuggestion}
                    onToggleChunk={handleToggleNeoFeedChunk}
                    onChangeUsageMode={handleChangeNeoFeedUsageMode}
                    onRemoveSource={handleRemoveNeoFeedSource}
                />
            ) : (
                <Typography sx={{ fontSize: '0.82rem', color: '#999', textAlign: 'center', py: 3 }}>
                    Ative "Usar base NeoFeed" acima para ver sugestões do acervo.
                </Typography>
            ),
        },
        {
            key: 'related',
            label: 'Notícias relacionadas',
            selectedCount: selectedRelatedIds.length,
            summaryItems: selectedRelatedSummaryItems,
            onClearAll: handleClearRelatedSelection,
            panel: (
                <RelatedNewsPanel
                    embedded
                    items={relatedNews}
                    total={relatedNewsTotal}
                    loading={relatedNewsLoading}
                    page={relatedNewsPage}
                    pageSize={RELATED_PAGE_SIZE}
                    onPageChange={setRelatedNewsPage}
                    selectedIds={selectedRelatedIds}
                    onToggle={toggleRelatedNews}
                />
            ),
        },
    ];

    return (
        <>
            <ArticleWriter
                backLabel="Voltar as matérias"
                onBack={handleBack}
                extraTopActions={extraTopActions}
                headerContent={headerContent}
                loading={pageLoading}
                resetToken={editorial_intelligence_id}
                initialArticlesHistory={articlesHistory}
                initialArticle={latestArticle}
                initialResponseId={latestArticle?.response_id ?? null}
                streamFn={streamFn}
                onGenerated={handleGenerated}
                extraPayload={extraPayload}
                autoGenerateWhenEmpty
                contextSources={contextSources}
            />

            {/* ── Modal: Confirmação de exclusão ───────────────────────── */}
            <Dialog
                open={confirmDeleteOpen}
                onClose={() => !deleteLoading && setConfirmDeleteOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>
                    Confirmar exclusão
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Deseja realmente excluir a matéria <strong>{latestVersionTitle}</strong>? Esta ação não poderá ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setConfirmDeleteOpen(false)}
                        disabled={deleteLoading}
                        sx={{ color: '#666', textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleteLoading}
                        sx={{
                            backgroundColor: '#d32f2f', textTransform: 'none', boxShadow: 'none', borderRadius: '8px',
                            '&:hover': { backgroundColor: '#b71c1c' },
                        }}
                    >
                        {deleteLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
